import React from "react";
import { Box, Typography } from "@material-ui/core";
import { WEB_FORM_FIELD_INPUT_TYPE, isLabelOnlyField } from "../../constants/webFormFields";

/**
 * Vista previa en vivo del formulario (mejora post-MVP, 2026-09-19): sin
 * ella, quien ve el Builder por primera vez no tiene forma de saber cómo
 * quedará su formulario para un visitante real — solo un editor de campos
 * en abstracto.
 *
 * Se actualiza al toque con cada cambio de `fields`/`style`, SIN guardar ni
 * tocar el backend — no necesita que el formulario ya tenga `hash` (a
 * diferencia de una alternativa con iframe + `embed.js` real, que solo
 * podría mostrarse DESPUÉS de crear el formulario). Es una aproximación en
 * React, no el pixel-perfect exacto del script — pero usa el MISMO mapeo
 * tipo→input (`WEB_FORM_FIELD_INPUT_TYPE`) y los mismos valores de estilo
 * que `embed.js` de verdad lee, así que queda muy cercana.
 */
const FormPreview = ({ name, fields, styleJson, services }) => {
  const style = styleJson || {};
  const backgroundColor = style.backgroundColor || "#ffffff";
  const borderColor = style.borderColor || "#e2e2e2";
  const buttonColor = style.buttonColor || "#25D366";
  const fontFamily = style.fontFamily || "system-ui, -apple-system, sans-serif";

  return (
    <Box>
      <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
        Así lo verá el visitante
      </Typography>
      <Box
        style={{
          background: "#f0f0f3",
          borderRadius: 12,
          padding: 20,
          display: "flex",
          justifyContent: "center"
        }}
      >
        <Box
          component="form"
          onSubmit={e => e.preventDefault()}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            padding: 16,
            background: backgroundColor,
            border: `1px solid ${borderColor}`,
            borderRadius: 8,
            width: "100%",
            maxWidth: 360,
            fontFamily
          }}
        >
          {style.logoUrl && (
            <img
              src={style.logoUrl}
              alt="Logo"
              style={{ maxHeight: 56, maxWidth: "100%", objectFit: "contain", alignSelf: "center" }}
            />
          )}
          {fields.length === 0 ? (
            <Typography variant="body2" style={{ color: "#999" }}>
              Agrega campos para verlos aquí.
            </Typography>
          ) : (
            fields.map((field, index) => {
              const label = field.etiqueta || field.tipo;

              // Texto decorativo fijo — sin caja de entrada, el visitante
              // solo lo lee (título/subtítulo dentro del formulario).
              if (isLabelOnlyField(field.tipo)) {
                return (
                  <Typography key={`${field.tipo}-${index}`} style={{ fontSize: 15, fontWeight: 700, color: "#222" }}>
                    {label}
                  </Typography>
                );
              }

              const inputType = WEB_FORM_FIELD_INPUT_TYPE[field.tipo] || "text";
              // Mismo criterio que `embedScript.ts`: "servicio" se pinta
              // como lista desplegable SOLO si la empresa configuró su
              // catálogo real en Agenda de Citas — si no, sigue siendo texto
              // libre, aquí y en el script real (paridad preview/real).
              const isServiceField = field.tipo === "servicio" && Array.isArray(services) && services.length > 0;
              return (
                <Box key={`${field.tipo}-${index}`}>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#222", marginBottom: 4 }}>
                    {label}
                    {field.obligatorio && <span style={{ color: "#b00020" }}> *</span>}
                  </label>
                  {isServiceField ? (
                    <select
                      disabled
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        border: "1px solid #ccc",
                        borderRadius: 6,
                        fontSize: 14,
                        fontFamily: "inherit",
                        boxSizing: "border-box"
                      }}
                    >
                      <option>Selecciona una opción</option>
                      {services.map(serviceName => (
                        <option key={serviceName}>{serviceName}</option>
                      ))}
                    </select>
                  ) : inputType === "textarea" ? (
                    <textarea
                      disabled
                      rows={3}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        border: "1px solid #ccc",
                        borderRadius: 6,
                        fontSize: 14,
                        fontFamily: "inherit",
                        resize: "none",
                        boxSizing: "border-box"
                      }}
                    />
                  ) : (
                    <input
                      type={inputType}
                      disabled
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        border: "1px solid #ccc",
                        borderRadius: 6,
                        fontSize: 14,
                        fontFamily: "inherit",
                        boxSizing: "border-box"
                      }}
                    />
                  )}
                </Box>
              );
            })
          )}
          <button
            type="submit"
            disabled
            style={{
              padding: "10px 14px",
              border: "none",
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              background: buttonColor,
              color: "#ffffff",
              cursor: "default"
            }}
          >
            Enviar
          </button>
        </Box>
      </Box>
    </Box>
  );
};

export default FormPreview;
